<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { createEventDispatcher } from "svelte";

    import EmptyElement from "@components/EmptyElement.svelte";
    import Icon from "@components/Icon.svelte";
    import CartIcon from "@components/icons/CartIcon.svelte";
    import SoldOutIcon from "@components/icons/SoldOutIcon.svelte";
    import UnavailableIcon from "@components/icons/UnavailableIcon.svelte";
    import Money from "@components/Money.svelte";
    import Spinner from "@components/Spinner/Spinner.svelte";
    import T from "@components/T.svelte";

    import PostModalButton from "./PostModalButton.svelte";
    import type { SelectedVariantInterface } from "./types";

    export let selectedVariant: SelectedVariantInterface | null;
    export let productTitle: string;
    export let productUrl: string;
    export let cartStatus: STATUS;
    export let isCartAvailable: boolean;
    const dispatch = createEventDispatcher();
    const addToCard = () => {
        dispatch("addToCard");
    };
    const handleCheckout = () => {
        dispatch("handleCheckout");
    };
</script>

<div class="gw-post-modal-product-info">
    <h3 class="gw-post-modal-product-info__title">
        <a
            href={productUrl}
            class="gw-link-reset-default-styles gw-post-modal-product-info__link"
            aria-label={`Opens the product ${productTitle} in a new tab`}
            rel="noopener noreferrer"
            target="_blank"
        >
            <p class="gw-post-modal-product-info__title-text gw-default-reset">
                {productTitle}
            </p>
        </a>
    </h3>
    <div class="gw-post-modal-product-info__line">
        <div class="gw-post-modal-product-info__price-wrapper">
            {#if selectedVariant}
                <span class="gw-post-modal-product-info__price">
                    <Money amount={selectedVariant.price} with_currency />
                </span>
                {#if selectedVariant.compareAtPrice}
                    <span class="gw-post-modal-product-info__compare-price">
                        <del>
                            <Money
                                amount={selectedVariant.compareAtPrice}
                                with_currency
                            />
                        </del>
                    </span>
                {/if}
            {:else}
                <EmptyElement />
            {/if}
        </div>
    </div>
    <slot />
    <div class="gw-post-modal-product-info__button">
        {#if isCartAvailable}
            <PostModalButton
                disabled={!selectedVariant || !selectedVariant.available}
                on:click={cartStatus === STATUS.READY
                    ? handleCheckout
                    : addToCard}
                ariaLabel="Add to card"
            >
                {#if cartStatus === STATUS.LOADING}
                    <Spinner size="18px" />
                {:else}
                    <Icon
                        width={14}
                        height={17}
                        class={!selectedVariant
                            ? "gw-post-modal-product-info__button-icon"
                            : ""}
                    >
                        {#if selectedVariant}
                            {#if selectedVariant.available}
                                <CartIcon />
                            {:else}
                                <SoldOutIcon />
                            {/if}
                        {:else}
                            <UnavailableIcon />
                        {/if}
                    </Icon>
                {/if}

                <span class="gw-post-modal-product-info__button-text">
                    {#if cartStatus === STATUS.LOADING}
                        <T
                            namespace="common.PostModal"
                            translationKey="adding_to_cart"
                        />
                    {:else if cartStatus === STATUS.READY}
                        <T
                            namespace="common.PostModal"
                            translationKey="checkout"
                        />
                    {:else if selectedVariant}
                        {#if selectedVariant.available}
                            <T
                                namespace="common.PostModal"
                                translationKey="add_to_cart"
                            />
                        {:else}
                            <T
                                namespace="common.PostModal"
                                translationKey="sold_out"
                            />
                        {/if}
                    {:else}
                        <T
                            namespace="common.PostModal"
                            translationKey="unavailable"
                        />
                    {/if}
                </span>
            </PostModalButton>
        {/if}
    </div>
</div>

<style>
    .gw-post-modal-product-info {
        padding: 24px 0 32px;
    }

    .gw-post-modal-product-info__title {
        margin: 0;
        margin-bottom: 20px;
        font-size: 1.6em;
    }

    .gw-post-modal-product-info__title-text {
        color: var(--gw-post-modal-text-color);
    }

    .gw-post-modal-product-info__line {
        display: flex;
        justify-content: space-between;
        margin-bottom: 16px;
    }

    .gw-post-modal-product-info__price-wrapper {
        display: flex;
        align-items: center;
        column-gap: 16px;
    }

    .gw-post-modal-product-info__price {
        color: var(--gw-post-modal-text-color);
        font-size: 1.75em;
        font-weight: var(--gw-font-weight-semibold);
    }

    .gw-post-modal-product-info__compare-price {
        opacity: 0.7;
        color: var(--gw-post-modal-text-color);
    }

    .gw-post-modal-product-info__button {
        display: flex;
        height: 40px;
    }

    .gw-post-modal-product-info__button {
        color: var(--gw-dark-text-color);
        --gw-spinner-size: 14px;
        --gw-spinner-color: #fff;
    }

    .gw-post-modal-product-info__button-text {
        text-transform: capitalize;
    }

    @media all and (max-width: 425px) {
        .gw-post-modal-product-info__line {
            flex-direction: column;
            gap: 10px;
        }
    }
</style>
